import { ResizerButton, SidebarResizer, Wrapper } from './DsktopView.styled';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sidebar } from './components/Sidebar/Sidebar';
import { faCaretLeft, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Content } from './content/Content';
import { useDesktopContext } from 'contexts/DesktopContext';
import { useAreasQuery } from 'hooks/queries/useAreasQuery';
import { useTeamsQuery } from 'hooks/queries/useTeamsQuery';
import { useAccount } from 'hooks/useAccount';
import { useWindowWidth } from 'hooks/useWindowWidth';

export const DesktopView = () => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const windowWidth = useWindowWidth();

  const { loggedUserData } = useAccount();

  const { setAreas, setFilters, setMyTeams, selectedTeam } =
    useDesktopContext();
  const { data: areasData = [] } = useAreasQuery();
  const { data: teamsData = [] } = useTeamsQuery();

  const startResizing = useCallback(() => {
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = useCallback(
    (mouseMoveEvent: unknown) => {
      if (isResizing) {
        const val =
          // @ts-ignore
          mouseMoveEvent?.clientX -
          // @ts-ignore
          sidebarRef?.current?.getBoundingClientRect().left;

        val > 250 && val < 400 && setSidebarWidth(val);
      }
    },
    [isResizing]
  );

  useEffect(() => {
    if (areasData.length) setAreas(areasData);
  }, [areasData]);

  useEffect(() => {
    setMyTeams(
      teamsData.filter((team) =>
        loggedUserData.teams.find((userTeam) => userTeam.id === team.id)
      )
    );
  }, [teamsData, loggedUserData.teams, setMyTeams]);

  const loadSelectedAreas = useCallback(() => {
    const nextIds = selectedTeam?.areas?.length
      ? selectedTeam.areas.map((area) => area.id)
      : (loggedUserData.areas ?? []).map((userArea) => userArea.id);

    setFilters((prevFilters) => {
      const prevIds = prevFilters.area_ids?.value ?? [];
      if (
        prevIds.length === nextIds.length &&
        prevIds.every((id, index) => id === nextIds[index])
      ) {
        return prevFilters;
      }
      return {
        ...prevFilters,
        area_ids: { value: nextIds }
      };
    });
  }, [loggedUserData.areas, selectedTeam, setFilters]);

  useEffect(() => {
    loadSelectedAreas();
  }, [loadSelectedAreas]);

  useEffect(() => {
    setSidebarOpen(windowWidth > 768);
  }, [windowWidth]);

  useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [resize, stopResizing]);

  const sidebarMemo = useMemo(() => <Sidebar />, []);

  return (
    <Wrapper>
      <div
        ref={sidebarRef}
        style={{ width: sidebarWidth, display: sidebarOpen ? 'block' : 'none' }}
      >
        {sidebarMemo}
      </div>
      <SidebarResizer onMouseDown={startResizing}>
        <ResizerButton
          onClick={() => setSidebarOpen((prevState) => !prevState)}
        >
          <FontAwesomeIcon icon={sidebarOpen ? faCaretLeft : faCaretRight} />
        </ResizerButton>
      </SidebarResizer>
      <Content />
    </Wrapper>
  );
};
